import { useEffect, useRef } from "react";

// Minimum jarak waktu antar-fetch (ms) — mencegah stampede saat user pindah tab
// bolak-balik atau multiple pages trigger focus event bersamaan.
const MIN_FETCH_INTERVAL_MS = 30_000;

// Timestamp fetch terakhir per loader function — di-share global agar
// stampede dari komponen berbeda (Dashboard, Bell, page list) tetap terkontrol.
const _lastFetchAt = new WeakMap();

/**
 * useAutoRefresh — menjaga data selalu segar tanpa reload page atau logout.
 *
 * Trigger fetch pada:
 *   1. Mount pertama.
 *   2. Window/tab kembali fokus (focus event) — throttled.
 *   3. Tab kembali visible (visibilitychange -> "visible") — throttled.
 *   4. Polling interval selama tab aktif (default 3 menit).
 *
 * Menghindari refetch saat tab tidak visible → hemat bandwidth di HP lapangan
 * dan hemat memory backend.
 *
 * @param {() => void | Promise<any>} loadFn — fungsi loader (idempotent).
 * @param {object} opts
 * @param {number} opts.interval — polling interval ms (default 180000 = 3 menit). 0 = disable.
 * @param {any[]} opts.deps — jika berubah, refetch (mirip useEffect deps).
 * @param {boolean} opts.enabled — jika false, hook tidak melakukan apa pun.
 */
export default function useAutoRefresh(loadFn, opts = {}) {
  const { interval = 300_000, deps = [], enabled = true } = opts;
  const fnRef = useRef(loadFn);
  fnRef.current = loadFn;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const throttledLoad = (forceFirst = false) => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const fn = fnRef.current;
      if (!fn) return;
      const now = Date.now();
      const last = _lastFetchAt.get(fn) || 0;
      // First call after mount → bypass throttle (agar page baru dibuka langsung dapat data).
      if (!forceFirst && now - last < MIN_FETCH_INTERVAL_MS) return;
      _lastFetchAt.set(fn, now);
      try { fn(); } catch (_) { /* loader punya error handling sendiri */ }
    };

    // 1) initial fetch (bypass throttle)
    throttledLoad(true);

    // 2) focus & visibility triggers (throttled)
    const onFocus = () => throttledLoad(false);
    const onVisibility = () => {
      if (document.visibilityState === "visible") throttledLoad(false);
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    // 3) polling (jeda saat tab hidden — dicek di throttledLoad)
    let timer = null;
    if (interval > 0) {
      timer = setInterval(() => throttledLoad(false), interval);
    }

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, interval, ...deps]);
}
